import { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowUpRight, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { CLIENT } from "@/constants/testIds";
import Seo from "@/components/Seo";

const EASE = [0.76, 0, 0.24, 1];

const formatDetail = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  return String(d);
};

export default function ClientLoginPage() {
  const { user, signIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    const from = location.state?.from || "/client";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await signIn(email, password);
      if (u.role !== "client") {
        setError("This login is for hiring-partner accounts. Try /admin/login for admins.");
        toast.error("Wrong portal.");
        return;
      }
      toast.success(`Welcome, ${u.name}.`);
      navigate(location.state?.from || "/client", { replace: true });
    } catch (err) {
      const msg = formatDetail(err?.response?.data?.detail) || err?.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-16 bg-[var(--bg)] flex items-start justify-center">
      <Seo title="Hiring partner sign-in" description="Orphion hiring-partner portal." path="/client/login" noIndex />
      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="md:col-span-6"
        >
          <p className="overline mb-6">Hiring partner · Sign in</p>
          <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.92] text-[var(--ink)]">
            Your{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              shortlist
            </span>{" "}
            is ready.
          </h1>
          <p className="mt-8 text-[var(--ink-80)] max-w-md leading-relaxed">
            Sign in with the credentials your Orphion engagement manager sent
            you. You&apos;ll see the live pipeline for every role assigned to
            your company — match scores, CV summaries, contact details.
          </p>
          <div className="mt-14 border-t border-[var(--line-strong)] pt-8 flex items-center gap-3 text-[var(--ink-50)]">
            <Briefcase className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.24em]">
              Client-only · Encrypted
            </span>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
          data-testid={CLIENT.loginForm}
          className="md:col-span-6 md:col-start-8 space-y-2"
        >
          <div>
            <label className="overline block mb-2">Work email</label>
            <input
              required
              type="email"
              className="editorial-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              data-testid={CLIENT.loginEmail}
            />
          </div>
          <div className="pt-4">
            <label className="overline block mb-2">Password</label>
            <input
              required
              type="password"
              className="editorial-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              data-testid={CLIENT.loginPassword}
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm pt-4" data-testid={CLIENT.loginError}>
              {error}
            </div>
          )}

          <div className="pt-10">
            <button
              type="submit"
              disabled={loading}
              className="btn-pill disabled:opacity-60"
              data-testid={CLIENT.loginSubmit}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Signing in…</span>
                </>
              ) : (
                <>
                  <span>Enter portal</span>
                  <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
